import { AUTH, db, functions, httpsCallable } from '@util/firebase';
import { ChatDocument, ChatMessageDocument } from '@util/types/firestore/chat';
import { OfferDocument } from '@util/types/firestore/offers';
import {
  query,
  where,
  getDocs,
  collection,
  CollectionReference,
  getDoc,
  doc,
  setDoc,
  updateDoc,
  getCountFromServer,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
} from 'firebase/firestore';
import { formatCurrency } from '@util/index';
import { OrderDocument } from '@util/types/firestore/order';
import {
  ContactInfoDetectionResult,
  appendSafetyWarningToChat,
  detectForSender,
  sendContactInfoSlackAlert,
} from '../messages/contact-info-detection';
import { SUPPORT_ID } from '@util/firestore/messages/rate-limit';
import { requestChatMaskProof } from '../messages/mask-proof';

const messagesRef = collection(
  db,
  'messages'
) as CollectionReference<ChatDocument>;
const offersRef = collection(
  db,
  'offers'
) as CollectionReference<OfferDocument>;
const getDocRef = (id: string) => doc<OfferDocument>(offersRef, id);

// GB #2064 (Bill 2026-10-19: "offer notes count as chat"): offer and counter-offer
// notes are chat messages, so they get exactly what a chat message gets: the chat
// rule (including bare links), the GB #1942 exemption for admins, support, and
// trusted partners, and on a hit the Slack alert, the safety banner, and the chat
// flag. The server's onChatChanged applies the same rule; masking here first keeps
// its "client detection was bypassed" alert for writes that really skipped us.
async function getSenderRoles(uid: string) {
  try {
    // Roles come from the sender's own users doc (rules block writing your own
    // roles). If the read fails the note is masked: the safe side.
    const snap = await getDoc(doc(db, 'users', uid));
    return (snap.data() as { roles?: Parameters<typeof detectForSender>[1] } | undefined)?.roles;
  } catch {
    return undefined;
  }
}

async function checkOfferNote(note: string, senderUid: string | undefined) {
  return detectForSender(note, senderUid ? await getSenderRoles(senderUid) : undefined);
}

// Fire-and-forget, as in ChatModal: an alert failure never blocks the offer.
function alertOnOfferNote(
  chat: Pick<ChatDocument, 'id' | 'uids'>,
  senderUid: string,
  detection: ContactInfoDetectionResult,
  source: 'offer note' | 'counter-offer note'
) {
  if (!detection.shouldAlert || !chat.id) return;
  sendContactInfoSlackAlert(
    chat as ChatDocument,
    senderUid,
    detection.originalContent,
    // shown in the alert's "Detected" line, so support knows where the hit came from
    [...detection.detectedTypes, source]
  ).catch(console.error);
  if (detection.wasRedacted && detection.warningType) {
    appendSafetyWarningToChat(chat.id, detection.warningType).catch(console.error);
  }
}

export const getOfferId = () => {
  const newDocRef = doc(offersRef);
  const id = newDocRef.id;
  return id;
};

export async function createOffer(offer: OfferDocument, buyerNote?: string) {
  if (!offer.is_auction) {
    const chatId = doc(messagesRef).id;
    const message: ChatMessageDocument = {
      // GearFocus system account — the offer notice comes from GearFocus, not the
      // buyer — and renders as a centered system banner via is_system. GB #1317.
      uid: SUPPORT_ID,
      content: offer.message ?? '',
      is_img: false,
      created_at: Date.now(),
      skip_notification: true,
      is_system: true,
    };
    const messages: ChatMessageDocument[] = [message];
    // The buyer's personal note is a SEPARATE message from the buyer — never baked
    // into the GearFocus system banner (mirrors the counter-offer flow). GB #1317.
    const note = buyerNote?.trim();
    const noteDetection = note ? await checkOfferNote(note, offer.buyer_id) : undefined;
    if (note && noteDetection) {
      const proofId = await requestChatMaskProof({
        chatId, senderUid: offer.buyer_id, rawContent: note,
        wasRedacted: noteDetection.wasRedacted,
      });
      messages.push({
        ...(proofId ? { mask_proof_id: proofId } : {}),
        uid: offer.buyer_id,
        content: noteDetection.redactedContent,
        // Browser hints only; these fields do not authenticate the sender.
        had_fraud_mask: noteDetection.wasRedacted,
        fraud_mask_types: noteDetection.wasRedacted
          ? noteDetection.detectedTypes.filter(
              (type) => type !== 'social_platform' && type !== 'offplatform_mention'
            )
          : [],
        is_img: false,
        created_at: Date.now() + 1,
      });
    }
    const chat: ChatDocument = {
      id: chatId,
      product_id: offer.product_id,
      buyer_id: offer.buyer_id,
      seller_id: offer.seller_id,
      is_offer: true,
      uids: offer.uids,
      is_bot: false,
      flagged: noteDetection?.wasRedacted === true,
      from_web: true,
      last_time: Date.now(),
      created_at: Date.now(),
      messages,
      unread: {
        [offer.buyer_id]: true,
        [offer.seller_id]: true,
      },
    };
    offer.chat_id = await createChat(chat);
    if (noteDetection) alertOnOfferNote(chat, offer.buyer_id, noteDetection, 'offer note');

    const chatRef = doc(messagesRef, offer.chat_id);
    await updateDoc(chatRef, { offer_id: offer.id });
  }
  if (!offer.id) throw new Error('Offer ID is required');
  const docRef = getDocRef(offer.id);
  await setDoc(docRef, offer);
}

export async function createOfferIntent(amount: number, customer?: string) {
  const res = await httpsCallable<
    { amount: number; customer?: string },
    { pi_id: string; data: string }
  >(
    functions,
    'createOfferIntent'
  )({ amount, customer });
  return res.data;
}

export async function updateOfferIntent(args: OfferDocument) {
  const res = await httpsCallable<OfferDocument, OfferDocument>(
    functions,
    'updateOfferIntent'
  )(args);
  return res.data;
}

export async function acceptOffer(offer_id: string) {
  const res = await httpsCallable<{ offer_id: string }, OrderDocument>(
    functions,
    'acceptOffer'
  )({ offer_id });
  return res.data;
}

export async function counterOffer(
  offer_id: string,
  new_price: number,
  message?: string
) {
  const senderUid = AUTH.currentUser?.uid;
  const noteDetection = message ? await checkOfferNote(message, senderUid) : undefined;
  let proofId: string | undefined;
  if (noteDetection?.wasRedacted) {
    const offer = await getOffer(offer_id);
    proofId = await requestChatMaskProof({
      chatId: offer?.chat_id ?? '', senderUid: senderUid ?? '', rawContent: message ?? '',
      wasRedacted: true,
    });
  }
  const res = await httpsCallable<
    { offer_id: string; new_price: number; message?: string; mask_proof_id?: string },
    { success: boolean; new_price: number; new_tax: number; new_total: number }
  >(
    functions,
    'counterOffer'
  )({
    offer_id, new_price, message: noteDetection ? noteDetection.redactedContent : message,
    ...(proofId ? { mask_proof_id: proofId } : {}),
  });
  if (noteDetection?.shouldAlert && senderUid) {
    getOffer(offer_id)
      .then((o) => o?.chat_id && alertOnOfferNote({ id: o.chat_id, uids: o.uids }, senderUid, noteDetection, 'counter-offer note'))
      .catch(console.error);
  }
  return res.data;
}

export async function createChat(chat: ChatDocument) {
  const docRef = chat.id ? doc(messagesRef, chat.id) : doc(messagesRef);
  chat.id = docRef.id;
  await setDoc(docRef, chat);
  return docRef.id;
}

export async function getOffer(offer_id: string): Promise<OfferDocument> {
  const offerDoc = await getDoc(doc(offersRef, offer_id));
  return (offerDoc.data() as OfferDocument) ?? null;
}

export const getOfferMessage = (
  productTitle: string,
  price: number,
  shipping: number,
  tax: number
): string => {
  const priceStr = formatCurrency(price);
  const shippingStr = formatCurrency(shipping);
  // Banner only — the buyer's personal note is sent as a SEPARATE buyer message
  // (see createOffer) so it never renders inside GearFocus's system banner. GB #1317.
  return `**A Fellow Creator Wants Your Gear!**\n\nOffer received: **${priceStr}** for your ${productTitle}.\n(Shipping: ${shippingStr})`;
};

export async function getOfferChats(uid: string) {
  const q = query(
    messagesRef,
    where('uids', 'array-contains', uid),
    where('is_offer', '==', true)
  );
  const { docs } = await getDocs(q);
  return docs.map((d) => d.data());
}

export function setOfferState(id: string, state: number) {
  return updateDoc(getDocRef(id), { state });
}

export async function getOfferById(id: string) {
  const doc = await getDoc(getDocRef(id));
  return doc.data();
}
export async function getPendingOfferCount(seller_id: string) {
  const q = query(
    offersRef,
    where('seller_id', '==', seller_id),
    where('is_auction', '==', false),
    where('state', '==', 0)
  );
  const res = await getCountFromServer(q);
  return res.data()?.count ?? 0;
}

// Count pending offers where buyer needs to respond (counter offers from seller)
export async function getPendingBuyerOfferCount(buyer_id: string) {
  const q = query(
    offersRef,
    where('buyer_id', '==', buyer_id),
    where('pending_response_from', '==', 'buyer'),
    where('state', '==', 0)
  );
  const res = await getCountFromServer(q);
  return res.data()?.count ?? 0;
}

export async function getActiveOffers(uid: string) {
  const q = query(
    offersRef,
    where('buyer_id', '==', uid),
    where('state', '==', 0)
  );
  const { docs } = await getDocs(q);
  return docs.map((d) => d.data());
}

// Get all offers for a seller (pending, accepted, declined)
export async function getSellerOffers(seller_id: string) {
  const q = query(
    offersRef,
    where('seller_id', '==', seller_id),
    where('is_auction', '==', false)
  );
  const { docs } = await getDocs(q);
  return docs.map((d) => d.data());
}

// Get all offers for a buyer (pending, accepted, declined)
export async function getBuyerOffers(buyer_id: string) {
  const q = query(
    offersRef,
    where('buyer_id', '==', buyer_id),
    where('is_auction', '==', false)
  );
  const { docs } = await getDocs(q);
  return docs.map((d) => d.data());
}

// TODO: Phase 2 — admin actions require Cloud Function auth bypass + analytics attribution fix (see GearBoard #1038)

// Admin: get all offers site-wide with pagination (follows getAllBuyerOrdersPaginated pattern)
interface GetAllOffersArgs {
  isAdmin: boolean;
  filterUid?: string | null; // optional: view a specific user's offers
  lastKey?: string | null;
  statusFilter?: number | null; // 0=pending, 1=accepted, 2=declined, 3=expired
  directionFilter?: 'all' | 'received' | 'sent';
}

export const getAllOffersPaginated = async (args: GetAllOffersArgs) => {
  const { isAdmin, filterUid, lastKey, statusFilter, directionFilter } = args;
  if (!isAdmin) return { results: [] };

  const limitValue = 20;
  const lastDoc = lastKey
    ? await getDoc(doc(offersRef, lastKey))
    : null;

  const statusConstraints =
    statusFilter !== null && statusFilter !== undefined
      ? [where('state', '==', statusFilter)]
      : [];

  // No is_auction filter for admin — admin sees all offers (regular + auction)

  // Admin viewing a specific user's offers (as buyer or seller)
  if (filterUid) {
    const userConstraint =
      directionFilter === 'sent'
        ? [where('buyer_id', '==', filterUid)]
        : directionFilter === 'received'
        ? [where('seller_id', '==', filterUid)]
        : []; // 'all' for a specific user requires two queries

    // If filter is 'all' for a specific user, fetch both sides and merge
    if (directionFilter === 'all' || !directionFilter) {
      const buyerQ = query(
        offersRef,
        where('buyer_id', '==', filterUid),
        ...statusConstraints,
        orderBy('created', 'desc'),
        limit(limitValue)
      );
      const sellerQ = query(
        offersRef,
        where('seller_id', '==', filterUid),
        ...statusConstraints,
        orderBy('created', 'desc'),
        limit(limitValue)
      );
      const [buyerSnap, sellerSnap] = await Promise.all([
        getDocs(buyerQ),
        getDocs(sellerQ),
      ]);
      const combined = [
        ...buyerSnap.docs.map((d) => d.data()),
        ...sellerSnap.docs.map((d) => d.data()),
      ];
      // Deduplicate and sort by created desc
      const seen = new Set<string>();
      const deduped = combined.filter((o) => {
        if (!o.id || seen.has(o.id)) return false;
        seen.add(o.id);
        return true;
      });
      deduped.sort((a, b) => (b.created || 0) - (a.created || 0));
      return { results: deduped.slice(0, limitValue) };
    }

    const q = query(
      offersRef,
      ...userConstraint,
      ...statusConstraints,
      orderBy('created', 'desc'),
      ...(lastDoc ? [startAfter(lastDoc)] : []),
      limit(limitValue)
    );
    const snapshot = await getDocs(q);
    return { results: snapshot.docs.map((d) => d.data()) };
  }

  // Admin viewing ALL offers site-wide
  const q = query(
    offersRef,
    ...statusConstraints,
    orderBy('created', 'desc'),
    ...(lastDoc ? [startAfter(lastDoc)] : []),
    limit(limitValue)
  );
  const snapshot = await getDocs(q);
  return { results: snapshot.docs.map((d) => d.data()) };
};
